import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fixAssetUrl, canonicalBase } from "../lib/api";
import { useI18n } from "../lib/i18n";
import ProductCard from "../components/ProductCard";
import ReviewsSection from "../components/ReviewsSection";
import { ArrowRight, Zap, ShieldCheck, Headphones, Award, Lock, Mail, Check, BookOpen } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import SEO from "../components/SEO";

const HERO_VISUAL = "https://static.prod-images.emergentagent.com/jobs/acad5d38-61f1-4be0-8df0-bfd60fbe4e35/images/ab7fb33aaebb5ff435c797b3f2106012977bbbb40ff24a56c61215723526e106.png";
const AVATARS = [
  "https://images.unsplash.com/photo-1767175473698-859bc73e8e64?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwxfHxzbWlsaW5nJTIwcHJvZmVzc2lvbmFsJTIwcG9ydHJhaXQlMjBjbGVhbiUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzc5Mzk2MjYyfDA&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1733231291465-d86afc9336ac?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHw0fHxzbWlsaW5nJTIwcHJvZmVzc2lvbmFsJTIwcG9ydHJhaXQlMjBjbGVhbiUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzc5Mzk2MjYyfDA&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1733231291514-06895ab0f5e1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwzfHxzbWlsaW5nJTIwcHJvZmVzc2lvbmFsJTIwcG9ydHJhaXQlMjBjbGVhbiUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzc5Mzk2MjYyfDA&ixlib=rb-4.1.0&q=85",
];

export default function Home() {
  const { t } = useI18n();
  const [products, setProducts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [homeSections, setHomeSections] = useState(null); // null = loading, [] = none, [...] = sections
  const [homeBlog, setHomeBlog] = useState(null); // { enabled, title, subtitle, position, posts }
  // Initial settings = whatever App.js already cached. Prevents flash of English defaults.
  const [settings, setSettings] = useState(() => window.__siteSettings || null);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data));
    api.get("/reviews").then((r) => setReviews(r.data));
    api.get("/home-sections").then((r) => setHomeSections(r.data || [])).catch(() => setHomeSections([]));
    api.get("/home-blog").then((r) => setHomeBlog(r.data || null)).catch(() => setHomeBlog(null));
    api.get("/site-settings", { params: { _: Date.now() } }).then((r) => {
      window.__siteSettings = r.data;
      setSettings(r.data);
    });
    // Also subscribe to App.js's bumpSeoSettings event so we always have latest
    const handler = () => { if (window.__siteSettings) setSettings({ ...window.__siteSettings }); };
    window.addEventListener("seo-settings", handler);
    return () => window.removeEventListener("seo-settings", handler);
  }, []);

  // Block first paint until we have settings — eliminates the half-second flash.
  if (!settings) {
    return (
      <div data-testid="home-page" className="pt-10 pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse space-y-6">
            <div className="h-6 w-48 bg-zinc-100 rounded-full"/>
            <div className="h-16 w-3/4 bg-zinc-100 rounded-xl"/>
            <div className="h-16 w-2/3 bg-zinc-100 rounded-xl"/>
            <div className="h-6 w-1/2 bg-zinc-100 rounded-full"/>
            <div className="h-12 w-64 bg-zinc-100 rounded-full mt-6"/>
          </div>
        </div>
      </div>
    );
  }

  const byCategory = (cat) => products.filter((p) => p.category === cat);
  const byLabel = (lbl) => products.filter((p) => p.labels?.includes(lbl));

  return (
    <div data-testid="home-page">
      <SEO
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "WebSite",
            name: window.__siteSettings?.seo_site_name || "Správný Směr",
            url: canonicalBase(),
            potentialAction: {
              "@type": "SearchAction",
              target: `${canonicalBase()}/produkty?q={search_term_string}`,
              "query-input": "required name=search_term_string",
            },
          },
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            name: window.__siteSettings?.seo_site_name || "Správný Směr",
            url: canonicalBase(),
            logo: window.__siteSettings?.seo_og_image || `${canonicalBase()}/logo.png`,
            aggregateRating: {
              "@type": "AggregateRating",
              ratingValue: 5,
              bestRating: 5,
              worstRating: 1,
              reviewCount: 3,
            },
            review: [
              {
                "@type": "Review",
                author: { "@type": "Person", name: "Lukáš O." },
                reviewRating: { "@type": "Rating", ratingValue: 5, bestRating: 5 },
                reviewBody: "Transakcia prebehla úspešne. Odporúčam, rýchla odozva, výborná komunikácia, spoľahlivosť a spokojnosť.",
              },
              {
                "@type": "Review",
                author: { "@type": "Person", name: "Martin S." },
                reviewRating: { "@type": "Rating", ratingValue: 5, bestRating: 5 },
                reviewBody: "Transakce proběhla úspěšně. Doporučuji. Rychlost dodání a okamžitá komunikace. Nejlepší prodejce na kterého jsem za poslední dobu narazil.",
              },
              {
                "@type": "Review",
                author: { "@type": "Person", name: "Helena S." },
                reviewRating: { "@type": "Rating", ratingValue: 5, bestRating: 5 },
                reviewBody: "Vše co je v nabídce funguje bez nejmenšího problému. Doporučuji.",
              },
            ],
          },
        ]}
      />
      {/* HERO */}
      <section className="relative pt-10 pb-24 overflow-hidden hero-glow hero-aurora" data-testid="hero-section">
        {/* Animated aurora gradient orbs (decorative, pointer-events disabled) */}
        <div className="aurora aurora-blue" aria-hidden="true"></div>
        <div className="aurora aurora-purple" aria-hidden="true"></div>
        <div className="aurora aurora-orange" aria-hidden="true"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 border border-blue-100 text-xs font-semibold text-blue-700 mb-6">
                <Zap className="w-3.5 h-3.5"/> {settings?.hero_badge || "Okamžité doručení e-mailem"}
              </div>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight font-bold text-zinc-950 leading-[1.05] mb-6">
                {settings?.hero_title_line1 || "Digitální software"} <br/>{settings?.hero_title_line2 || "jednoduše"} <span className="gradient-text">{settings?.hero_title_highlight || "a"}</span> {settings?.hero_title_line3 || "bezpečně"}
              </h1>
              <p className="text-lg text-zinc-600 leading-relaxed max-w-xl mb-8">
                {settings?.hero_subtitle || t("hero_sub")}
              </p>
              <div className="flex flex-wrap gap-3 mb-10">
                <Link to="/produkty" className="btn-primary inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold" data-testid="hero-cta-products">{settings?.hero_cta1 || t("hero_cta1")} <ArrowRight className="w-4 h-4"/></Link>
                <Link to="/produkty?label=bestseller" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold border border-zinc-200 bg-white/70 backdrop-blur hover:bg-white transition-all" data-testid="hero-cta-bestsellers">{settings?.hero_cta2 || t("hero_cta2")}</Link>
              </div>
              <div className="flex flex-wrap items-center gap-6 text-sm text-zinc-600">
                {(settings?.home_hero_features || "Originální licence|Faktura PDF|CZ/SK podpora").split("|").map((f, i) => (
                  <div key={i} className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-600"/>{f}</div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-br from-blue-200/30 via-purple-200/30 to-orange-200/20 rounded-[40px] blur-2xl"/>
              <div className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-zinc-50 to-white border border-zinc-200 shadow-xl">
                <img src={fixAssetUrl(settings?.hero_image || HERO_VISUAL)} alt="Digital software" className="w-full h-auto"/>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="border-y border-zinc-100 bg-zinc-50/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              {icon: Zap, t: "Okamžité doručení", s: "Klíč do 2 minut"},
              {icon: ShieldCheck, t: "Ověřené licence", s: "100% originální"},
              {icon: Headphones, t: "CZ/SK podpora", s: "Po-Pá 9-17"},
              {icon: Award, t: "Tisíce zákazníků", s: "Hodnocení 4.9/5"},
            ].map((x, i) => (
              <div key={i} className="flex items-center gap-3" data-testid={`trust-${i}`}>
                <div className="w-10 h-10 rounded-xl bg-white border border-zinc-200 flex items-center justify-center"><x.icon className="w-5 h-5 text-blue-600"/></div>
                <div>
                  <div className="text-sm font-semibold">{x.t}</div>
                  <div className="text-xs text-zinc-500">{x.s}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CATALOG SECTIONS — admin-configured if any, else default rows */}
      {homeSections && homeSections.length > 0 ? (
        <>
          {(() => {
            const rows = [];
            const blogPos = (homeBlog && homeBlog.enabled && (homeBlog.posts || []).length > 0)
              ? Math.max(0, Math.min(homeSections.length, homeBlog.position ?? 2))
              : -1;
            homeSections.forEach((sec, i) => {
              if (i === blogPos) {
                rows.push(<BlogSection key="home-blog" blog={homeBlog} bg={i % 2 === 1 ? "bg-zinc-50" : ""}/>);
              }
              rows.push(
                <ProductSection
                  key={sec.id}
                  title={sec.title}
                  subtitle={sec.subtitle}
                  products={sec.products || []}
                  testid={`home-${sec.id}`}
                  bg={i % 2 === 1 ? "bg-zinc-50" : ""}
                />
              );
            });
            // Append blog at the end if position >= sections.length
            if (blogPos >= homeSections.length) {
              rows.push(<BlogSection key="home-blog-tail" blog={homeBlog} bg={homeSections.length % 2 === 1 ? "bg-zinc-50" : ""}/>);
            }
            return rows;
          })()}
          {/* CUSTOMER REVIEWS — strip between product sections */}
          <ReviewsSection compact/>
        </>
      ) : (
        <>
          {/* Default fallback sections (used until admin defines any home-section) */}
          <ProductSection title={t("bestsellers")} products={byLabel("bestseller")} testid="bestsellers"/>
          <ProductSection title={t("microsoft")} subtitle="Windows, Office, Visio, Project a další." products={products.filter(p => p.brand === "Microsoft")} testid="microsoft"/>
          {homeBlog && homeBlog.enabled && (homeBlog.posts || []).length > 0 && (
            <BlogSection blog={homeBlog} bg="bg-zinc-50"/>
          )}
          <ReviewsSection compact/>
          <ProductSection title={t("adobe")} subtitle="Acrobat, Creative Cloud a kreativní nástroje." products={byCategory("adobe")} testid="adobe" bg="bg-zinc-50"/>
          <ProductSection title={t("protection")} subtitle="Antivir, ochrana a optimalizace PC." products={[...byCategory("antivirus"), ...byCategory("utilities")]} testid="protection"/>
          <ProductSection title={t("bundles")} subtitle="Ušetřete s našimi balíky." products={byCategory("bundle")} testid="bundles" bg="bg-gradient-to-br from-blue-50/60 to-purple-50/40"/>
        </>
      )}

      {/* WHY US — BENTO */}
      <section className="py-24" data-testid="why-us-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="text-xs uppercase tracking-widest text-blue-600 font-semibold mb-3">{settings?.home_why_subtitle || "Proč nás vybrat"}</div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">{settings?.home_why_title || "Důvěra postavená na detailech"}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <div className="md:col-span-2 rounded-3xl bg-gradient-to-br from-blue-600 to-indigo-700 p-8 text-white relative overflow-hidden">
              <div className="absolute -right-12 -bottom-12 w-64 h-64 bg-white/10 rounded-full"/>
              <Zap className="w-9 h-9 mb-5"/>
              <div className="font-display font-bold text-2xl mb-2">{settings?.home_why_b1_title || "Doručení do 2 minut"}</div>
              <div className="text-white/80 max-w-md">{settings?.home_why_b1_text || "Po platbě obdržíte klíč e-mailem a v sekci Můj účet."}</div>
            </div>
            <div className="rounded-3xl bg-white border border-zinc-200 p-8">
              <Lock className="w-9 h-9 text-orange-600 mb-5"/>
              <div className="font-display font-bold text-xl mb-2">{settings?.home_why_b2_title || "Šifrovaný trezor"}</div>
              <div className="text-sm text-zinc-600">{settings?.home_why_b2_text || "Vaše licence jsou v účtu uchovány šifrované a vždy dostupné."}</div>
            </div>
            <div className="rounded-3xl bg-white border border-zinc-200 p-8">
              <Award className="w-9 h-9 text-emerald-600 mb-5"/>
              <div className="font-display font-bold text-xl mb-2">{settings?.home_why_b3_title || "Originální klíče"}</div>
              <div className="text-sm text-zinc-600">{settings?.home_why_b3_text || "Spolupracujeme jen s ověřenými distributory."}</div>
            </div>
            <div className="rounded-3xl bg-white border border-zinc-200 p-8">
              <Headphones className="w-9 h-9 text-purple-600 mb-5"/>
              <div className="font-display font-bold text-xl mb-2">{settings?.home_why_b4_title || "Lidská podpora"}</div>
              <div className="text-sm text-zinc-600">{settings?.home_why_b4_text || "Žádné chatboty. Skuteční technici v ČJ/SK přímo od 9 do 17."}</div>
            </div>
            <div className="rounded-3xl bg-zinc-950 text-white p-8 relative overflow-hidden">
              <Mail className="w-9 h-9 text-orange-400 mb-5"/>
              <div className="font-display font-bold text-xl mb-2">{settings?.home_why_b5_title || "Faktura ihned"}</div>
              <div className="text-sm text-white/70">{settings?.home_why_b5_text || "Daňový doklad pro firmy automaticky v PDF."}</div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-24" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <div className="text-xs uppercase tracking-widest text-blue-600 font-semibold mb-3">FAQ</div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">{t("faq")}</h2>
          </div>
          <Accordion type="single" collapsible className="space-y-3">
            {[
              {q: "Jak rychle obdržím licenční klíč?", a: "Po úspěšné platbě je klíč automaticky odeslán na váš e-mail do 2 minut a zároveň se objeví v sekci Můj účet → Licence."},
              {q: "Jsou klíče originální?", a: "Ano, všechny licence jsou 100% originální a pocházejí od ověřených distributorů. Můžete je legálně aktivovat přímo u výrobce."},
              {q: "Mohu obdržet fakturu?", a: "Ano. Faktura PDF je automaticky vystavena a dostupná ke stažení po zaplacení. Podporujeme i firemní údaje (IČO, DIČ)."},
              {q: "Co když klíč nefunguje?", a: "V takovém případě nás kontaktujte – do 24 hodin nabídneme bezplatnou výměnu nebo plnou refundaci."},
              {q: "Jaké platby přijímáte?", a: "Platební karty (Visa, Mastercard), Apple Pay, Google Pay a bankovní převod."},
            ].map((item, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="bg-white rounded-2xl border border-zinc-200 px-6">
                <AccordionTrigger className="text-left font-semibold hover:no-underline" data-testid={`faq-q-${i}`}>{item.q}</AccordionTrigger>
                <AccordionContent className="text-zinc-600 leading-relaxed">{item.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>
    </div>
  );
}

function ProductSection({ title, subtitle, products, testid, bg }) {
  if (!products || products.length === 0) return null;
  return (
    <section className={`py-20 ${bg || ""}`} data-testid={`section-${testid}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between mb-8 gap-6">
          <div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">{title}</h2>
            {subtitle && <p className="text-zinc-600 mt-2">{subtitle}</p>}
          </div>
          <Link to="/produkty" className="hidden sm:inline-flex items-center gap-1 text-sm font-semibold text-blue-700 hover:text-blue-800">Zobrazit vše <ArrowRight className="w-4 h-4"/></Link>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {products.slice(0, 4).map((p) => <ProductCard key={p.id} product={p}/>)}
        </div>
      </div>
    </section>
  );
}

function BlogSection({ blog, bg }) {
  if (!blog || !blog.enabled) return null;
  const posts = (blog.posts || []).slice(0, 3);
  if (!posts.length) return null;
  const gridCols = posts.length === 1 ? "sm:grid-cols-1 lg:grid-cols-1 max-w-xl mx-auto" : posts.length === 2 ? "sm:grid-cols-2 lg:grid-cols-2 max-w-4xl mx-auto" : "sm:grid-cols-2 lg:grid-cols-3";
  return (
    <section className={`py-20 ${bg || ""}`} data-testid="section-home-blog">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between mb-8 gap-6">
          <div>
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-blue-600 font-semibold mb-3">
              <BookOpen className="w-3.5 h-3.5"/> Blog
            </div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">{blog.title}</h2>
            {blog.subtitle && <p className="text-zinc-600 mt-2 max-w-2xl">{blog.subtitle}</p>}
          </div>
          <Link to="/blog" className="hidden sm:inline-flex items-center gap-1 text-sm font-semibold text-blue-700 hover:text-blue-800" data-testid="home-blog-all-link">Všechny články <ArrowRight className="w-4 h-4"/></Link>
        </div>
        <div className={`grid grid-cols-1 ${gridCols} gap-6`}>
          {posts.map((p) => (
            <Link key={p.id} to={`/blog/${p.slug}`} className="group product-card overflow-hidden rounded-3xl flex flex-col" data-testid={`home-blog-post-${p.slug}`}>
              <div className="aspect-[16/10] bg-gradient-to-br from-blue-50 via-white to-purple-50 overflow-hidden relative">
                {p.cover_image ? (
                  <img src={fixAssetUrl(p.cover_image)} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" onError={(e) => { e.currentTarget.style.display = "none"; }}/>
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-blue-300">
                    <BookOpen className="w-14 h-14"/>
                  </div>
                )}
              </div>
              <div className="p-5 flex-1 flex flex-col">
                <h3 className="font-display font-bold text-lg leading-snug tracking-tight group-hover:text-blue-700 transition-colors line-clamp-2">{p.title}</h3>
                {p.excerpt && <p className="text-sm text-zinc-600 mt-2 line-clamp-3 flex-1">{p.excerpt}</p>}
                <div className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-blue-700 group-hover:gap-2 transition-all">
                  Číst dále <ArrowRight className="w-4 h-4"/>
                </div>
              </div>
            </Link>
          ))}
        </div>
        <div className="sm:hidden mt-6 text-center">
          <Link to="/blog" className="inline-flex items-center gap-1 text-sm font-semibold text-blue-700 hover:text-blue-800">Všechny články <ArrowRight className="w-4 h-4"/></Link>
        </div>
      </div>
    </section>
  );
}
